import api from '../../../api/client';
import React, { useRef, useState } from "react";
import { Button, Space, message, Card, Modal, ConfigProvider } from "antd";
import {
  PlusCircleOutlined,
  ExportOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import ProTable from "@ant-design/pro-table";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import axios from "axios";
import config from "../../../config";
import { usePermissions } from "../../../permissions";
import FarmFormModal from "./FarmFormModal"; // the modal component we'll create
import { useSelector } from "react-redux";
import enUS from "antd/lib/locale/en_US";
import { formatFullDate } from "../../../utils/dateUtils";

const FarmList = () => {
  const account = useSelector((state) => state.account);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const actionRef = useRef();
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const { createPermission, deletePermission, editPermission, viewPermission } =
    usePermissions();

  // API call to fetch farms
  const fetchFarms = async (params) => {
    try {
      const { current, pageSize, ...filters } = params;
      const response = await api.get(`/farm`, {
        params: {
          page: current,
          limit: pageSize,
          search: filters.name || filters.id,
        },
      });
      return {
        data: response.data.data || response.data,
        success: true,
        total: response.data.total || response.data.length,
      };
    } catch (error) {
      message.error("Failed to load farms");
      return { data: [], success: false, total: 0 };
    }
  };

  const handleDelete = async (id, name) => {
    Modal.confirm({
      title: "Confirm Archive",
      content: `Are you sure you want to archive ${name}?`,
      onOk: async () => {
        try {
          await api.put(`/farm/${id}/archive`);
          message.success("Farm archived successfully");
          actionRef.current?.reload();
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const exportToCSV = () => {
    // You can fetch all data or use current table data
    // For simplicity, we export the current page data (but you can enhance)
    actionRef.current?.exportData?.("csv");
  };

  const exportToPDF = async () => {
    const { data } = await fetchFarms({ current: 1, pageSize: 1000 });
    const doc = new jsPDF();
    doc.autoTable({
      head: [["ID", "Farm Name", "Created At", "Age"]],
      body: data.map((item) => [
        item.id,
        item.name,
        new Date(item.createdAt).toLocaleString(),
        calculateAge(item.createdAt),
      ]),
    });
    doc.save("farms.pdf");
  };

  const calculateAge = (date) => {
    const today = new Date();
    const birth = new Date(date);
    const diffDays = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays < 30) return `${diffDays} day(s) ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month(s) ago`;
    return `${Math.floor(diffDays / 365)} year(s) ago`;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      search: false,
    },
    {
      title: "Farm Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      valueType: "dateTime",
      search: false,
      render: (_, record) => formatFullDate(record.createdAt),
    },
    {
      title: "Age",
      key: "age",
      search: false,
      render: (_, record) => calculateAge(record.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      search: false,
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Button
              type="link"
              onClick={() => {
                // open modal in edit mode – you can pass record data
                setModalVisible(true);
                // you'd need to store editing record state; omitted for brevity
              }}
            >
              Edit
            </Button>
          )}
          {deletePermission && (
            <Button
              type="link"
              danger
              onClick={() => handleDelete(record.id, record.name)}
            >
              Archive
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const toolBarRender = () => [
    createPermission && (
      <Button
        key="add"
        type="primary"
        icon={<PlusCircleOutlined />}
        onClick={() => setModalVisible(true)}
      >
        Add Farm
      </Button>
    ),
    <Button key="csv" icon={<ExportOutlined />} onClick={exportToCSV}>
      Export CSV
    </Button>,
    <Button key="pdf" icon={<FilePdfOutlined />} onClick={exportToPDF}>
      Export PDF
    </Button>,
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <Card title="Farm Management" bordered={false}>
      <>
        <ProTable
          actionRef={actionRef}
          rowKey="id"
          columns={columns}
          request={fetchFarms}
          rowSelection={rowSelection}
          toolBarRender={toolBarRender}
          defaultSize={"small"}
          search={{
            labelWidth: "auto",
            defaultCollapsed: false,
            optionRender: ({ searchText, resetText }, { form }) => [
              <Button key="search" type="primary" onClick={() => form.submit()}>
                {searchText}
              </Button>,
              <Button key="reset" onClick={() => form.resetFields()}>
                {resetText}
              </Button>,
            ],
          }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          dateFormatter="string"
          headerTitle="All Farms"
          options={{ reload: true, density: true, setting: true }}
        />
      </>
      <FarmFormModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSuccess={() => {
          actionRef.current?.reload();
          setModalVisible(false);
        }}
      />
    </Card>
  );
};

export default FarmList;
